/**
 * DepotManagersPanel — Panneau des responsables de site (refonte 05/02/2026).
 *
 * Q1 retour user prod : « Les profils pour les responsables de site ne
 * devraient être visible que lors de l'étape de création ou de rachat ».
 *
 * Ce panneau affiche désormais UNIQUEMENT les managers déjà recrutés (et
 * affectés à un dépôt). Les profils disponibles à l'embauche n'apparaissent
 * que dans :
 *   • NewDepotModal.jsx (à l'ouverture d'un dépôt indépendant)
 *   • AcquisitionModal.jsx (au closing d'une M&A)
 *
 * Actions disponibles ici :
 *   • Ré-affecter un manager à un autre dépôt (déplacement interne)
 *   • Licencier un manager (le dépôt redevient non opérationnel)
 *
 * Le warning « dépôts non opérationnels » reste affiché en haut pour rappel.
 */
import React, { useState } from 'react';
import { Users, MapPin, AlertTriangle, X } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../store/stableEmpties';
import { DEPOT_MANAGERS, getDepotManagerById } from '../../data/depotManagers';
import { formatMoney } from '../../utils/format';
import AvatarWithFallback from '../AvatarWithFallback';

const SENIORITY_LABELS = {
  senior: 'Senior',
  confirmed: 'Confirmé',
  junior: 'Junior',
};

const SENIORITY_COLORS = {
  senior: 'bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/40',
  confirmed: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/40',
  junior: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
};

export default function DepotManagersPanel() {
  const ownedDepots = useGameStore(s => s.ownedDepots || EMPTY_ARRAY);
  const hiredManagers = useGameStore(s => s.hiredDepotManagers || EMPTY_OBJECT);
  const assignDepotManager = useGameStore(s => s.assignDepotManager);
  const fireDepotManager = useGameStore(s => s.fireDepotManager);
  const [assignTarget, setAssignTarget] = useState(null);

  const nonHqDepots = ownedDepots.filter(d => !d.isHeadquarters);
  const unstaffedDepots = nonHqDepots.filter(d => !d.managerId);
  // 05/02/2026 — N'affiche QUE les managers déjà recrutés. La liste des
  // candidats est gérée dans les modales de création/rachat.
  const hiredList = DEPOT_MANAGERS.filter(m => !!hiredManagers[m.id]);

  const handleAssign = (managerId, depotId) => {
    const r = assignDepotManager(managerId, depotId);
    if (!r.ok) window.alert(`Affectation refusée (${r.reason}).`);
    setAssignTarget(null);
  };

  const handleFire = (mgr) => {
    if (!window.confirm(`Licencier ${mgr.name} ? Indemnité ${mgr.monthlyCost.toLocaleString('fr-FR')} €.`)) return;
    fireDepotManager(mgr.id);
  };

  return (
    <div className="bg-ink-800 rounded-md border border-ink-600 p-4" data-testid="depot-managers-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-accent" />
          <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase">
            Mes responsables de site
          </h3>
          <span className="font-mono text-[10px] text-slate-500 tabular">
            [{hiredList.length}/{DEPOT_MANAGERS.length}]
          </span>
        </div>
      </div>

      {unstaffedDepots.length > 0 && (
        <div
          className="mb-3 p-2 rounded-md bg-amber-500/10 border border-amber-500/40 text-amber-700 dark:text-amber-300 text-xs flex items-start gap-2"
          data-testid="unstaffed-depots-warning"
        >
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <div>
            <strong>{unstaffedDepots.length} dépôt(s) sans responsable</strong> — non opérationnel(s) :
            ré-affectez un manager existant (ou licenciez puis recrutez via une nouvelle ouverture/rachat).
            <ul className="mt-1 text-[10px] font-mono">
              {unstaffedDepots.map(d => (
                <li key={d.id}>• {d.name || d.id}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {hiredList.length === 0 ? (
        <p className="text-xs text-slate-500 italic" data-testid="no-managers-hired">
          Aucun responsable recruté pour le moment. Les profils disponibles s&apos;affichent lors de l&apos;ouverture
          d&apos;un nouveau dépôt ou du closing d&apos;un rachat.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {hiredList.map(mgr => {
            const meta = hiredManagers[mgr.id] || {};
            const assignedDepotId = meta.assignedDepotId;
            const assignedDepot = assignedDepotId
              ? ownedDepots.find(d => d.id === assignedDepotId)
              : null;
            return (
              <div
                key={mgr.id}
                className="relative rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3 transition-all"
                data-testid={`depot-manager-card-${mgr.id}`}
              >
                <div className="flex items-start gap-3">
                  <AvatarWithFallback
                    src={mgr.portrait}
                    alt={mgr.name}
                    fallback={mgr.name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2)}
                    className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0"
                    testId={`mgr-avatar-${mgr.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <h4 className="text-sm font-bold text-foreground truncate">{mgr.name}</h4>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full border font-mono uppercase ${SENIORITY_COLORS[mgr.seniority]}`}>
                        {SENIORITY_LABELS[mgr.seniority]}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-500 font-mono">
                      {mgr.region} · {(mgr.languages || []).join(' / ').toUpperCase()}
                    </p>
                    <p className="text-[10px] text-amber-700 dark:text-amber-300 mt-1 font-medium">
                      ★ {mgr.bonus?.label}
                    </p>
                    <p className="text-[11px] font-mono text-slate-400 mt-1">
                      {formatMoney(mgr.monthlyCost)} <span className="text-slate-500">/mois</span>
                    </p>
                    {assignedDepot && (
                      <p className="text-[10px] text-emerald-300 mt-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> Affecté : {assignedDepot.name || assignedDepot.id}
                      </p>
                    )}
                    {!assignedDepot && (
                      <p className="text-[10px] text-amber-300 mt-1">⚠ Non affecté</p>
                    )}
                  </div>
                </div>

                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {nonHqDepots.length > 0 && (
                    <button
                      onClick={() => setAssignTarget(mgr.id)}
                      className="text-[10px] px-2.5 py-1 rounded-md border border-sky-500/40 text-sky-700 dark:text-sky-300 font-mono uppercase tracking-wider hover:bg-sky-500/10"
                      data-testid={`assign-mgr-${mgr.id}`}
                    >
                      {assignedDepot ? 'Déplacer' : 'Affecter'}
                    </button>
                  )}
                  <button
                    onClick={() => handleFire(mgr)}
                    className="text-[10px] px-2.5 py-1 rounded-md border border-danger/40 text-danger font-mono uppercase tracking-wider hover:bg-danger/10"
                    data-testid={`fire-mgr-${mgr.id}`}
                  >
                    Licencier
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal d'affectation */}
      {assignTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setAssignTarget(null)}
        >
          <div
            className="bg-ink-800 border border-ink-600 rounded-lg p-5 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-base font-bold text-foreground">
                Affecter {getDepotManagerById(assignTarget)?.name}
              </h4>
              <button
                onClick={() => setAssignTarget(null)}
                className="text-slate-400 hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-slate-400 mb-3">
              Choisissez le dépôt à manager. Le QG est exclu (c&apos;est vous le patron).
            </p>
            <div className="space-y-1.5">
              {nonHqDepots.length === 0 && (
                <p className="text-xs text-amber-300">Aucun dépôt non-QG à affecter. Ouvrez un nouveau dépôt d&apos;abord.</p>
              )}
              {nonHqDepots.map(d => {
                const existingMgr = d.managerId ? getDepotManagerById(d.managerId) : null;
                return (
                  <button
                    key={d.id}
                    onClick={() => handleAssign(assignTarget, d.id)}
                    className="w-full text-left p-2.5 rounded-md border border-ink-600 hover:border-sky-500/60 hover:bg-sky-500/5 transition-colors"
                    data-testid={`assign-mgr-to-${d.id}`}
                  >
                    <p className="text-sm font-bold text-foreground">{d.name || d.id}</p>
                    {existingMgr && (
                      <p className="text-[10px] text-amber-300 mt-0.5">
                        ⚠ Remplacera {existingMgr.name} (sera détaché)
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
